<template>
	<div v-if="ctaLink || (unavailableState && props.displayUnavailableState)" data-test="direct-invest-cta">
		<BaseLink v-if="ctaLink" :link="ctaLink" :classes="ctaLinkClasses" :data-test="ctaLink.text" />
		<div
			v-else-if="unavailableState && props.displayUnavailableState"
			class="closed-to-investments display-flex justify-content-center text-align-center p-50 text-color-content-secondary"
			:data-test="unavailableState"
			>{{ unavailableState }}</div
		>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { DirectInvestmentEligibility } from 'types/funds';
import { getInvestFlowLink } from '@utils/invest';
import { InvestmentTriggerQuery } from 'types/checkout';
import { Link } from 'types/layout';

// props
interface Props {
	classes?: string;
	directInvestmentEligibility: DirectInvestmentEligibility;
	displayUnavailableState?: boolean;
	offeringVintageId: string;
	theme?: 'dark' | 'light';
	trigger?: InvestmentTriggerQuery;
}

const props = withDefaults(defineProps<Props>(), {
	displayUnavailableState: false,
	classes: '',
	theme: 'light',
	trigger: undefined
});

const showDirectInvestmentLink = computed((): boolean => props.directInvestmentEligibility === 'ELIGIBLE');

const showAccreditationButton = computed(
	(): boolean =>
		props.directInvestmentEligibility === 'INELIGIBLE_REQUIRES_ACCREDITATION' ||
		props.directInvestmentEligibility === 'INELIGIBLE_FUND_LEAD_UP_REQUIRES_ACCREDITATION'
);

const showContactButton = computed(
	(): boolean =>
		props.directInvestmentEligibility === 'INELIGIBLE_ACCREDITED_NOT_IN_COHORT' ||
		props.directInvestmentEligibility === 'INELIGIBLE_IN_COHORT_CAP_EXCEEDED'
);

const showReservationEligibleButton = computed(
	(): boolean => props.directInvestmentEligibility === 'RESERVATION_ELIGIBLE'
);

const showComingSoon = computed(
	(): boolean => props.directInvestmentEligibility === 'INELIGIBLE_FUND_LEAD_UP_ACCREDITED'
);

const showFundClosed = computed((): boolean => props.directInvestmentEligibility === 'INELIGIBLE_FUND_CLOSED');

const showFundNotAvailable = computed((): boolean => props.directInvestmentEligibility === 'INELIGIBLE');

const investButtonLink = computed((): Link => {
	return getInvestFlowLink('fund', 'Invest', { offeringId: props.offeringVintageId, trigger: props.trigger });
});

const accreditationLink = computed((): Link => {
	return {
		text: 'Verify accreditation',
		router: 'accreditation'
	};
});

const contactLink = computed((): Link => {
	return {
		text: 'Contact Investor Relations',
		router: 'contact'
	};
});

const createReservationLink = computed((): Link => {
	return {
		text: 'Reserve a spot',
		router: 'account-invest-window-reservation-start',
		query: { offeringId: props.offeringVintageId, trigger: props.trigger }
	};
});

const ctaLink = computed((): Link | null => {
	let link: Link | null = null;
	if (showDirectInvestmentLink.value) {
		link = investButtonLink.value;
	} else if (showAccreditationButton.value) {
		link = accreditationLink.value;
	} else if (showContactButton.value) {
		link = contactLink.value;
	} else if (showReservationEligibleButton.value) {
		link = createReservationLink.value;
	}

	return link;
});

const unavailableState = computed((): string | null => {
	if (showComingSoon.value) {
		return 'Coming soon';
	} else if (showFundClosed.value) {
		return 'This fund is closed to investments.';
	} else if (showFundNotAvailable.value) {
		return 'This fund is not available for your account.';
	}

	return null;
});

const ctaColor = computed((): string => {
	if (showDirectInvestmentLink.value || showReservationEligibleButton.value) {
		return props.theme === 'light' ? 'button-default' : 'button-primary';
	} else {
		return props.theme === 'light' ? 'button-outline' : 'button-outline-reversed';
	}
});

const ctaLinkClasses = computed((): string =>
	props.classes ? `button no-wrap ${props.classes} ${ctaColor.value}` : `button no-wrap ${ctaColor.value}`
);
</script>

<style lang="scss">
@use '../../styles/constants/colors' as *;
.closed-to-investments {
	background-color: token('bg-secondary');
	border-radius: 2px;
}
</style>
